import { PlusIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Button from "../../components/Button";
import { Modal } from "../../components/Modal";
import { Toast } from "../../components/Toast";
import { ReactiveFormInput } from "../../components/form/ReactiveFormInput";
import { ReactiveFormSelect } from "../../components/form/ReactiveFormSelect";
import { RoleEnum, getRoles } from "../../enums/Role";
import { AgentCommissionRule } from "../../models/AgentCommissionRule";
import { CommissionRuleType } from "../../models/CommissionRuleType";
import { User } from "../../models/User";
import {
    createAgencyUserCommissionRule,
} from "../../state/agencies/action";
import { fetchCommissionRuleTypes } from "../../state/commissions/action";
import { AppDispatch } from "../../state/store";
import { on } from "events";

interface CreateRuleModalProps {
  isOpen: boolean;
  userId?: number;
  onClose: () => void;
  onSaving?: (saving: boolean) => void;
  onSaveCompleted?: (rules?: AgentCommissionRule[], error?: any) => void;
}

export const CreateRuleModal: React.FC<CreateRuleModalProps> = ({ isOpen, userId, onClose, onSaveCompleted, onSaving }) => {
  const { t } = useTranslation();
  const currentUser = useSelector((state: any) => state.users.currentUser);
  const dispatch = useDispatch<AppDispatch>();
  const error = useSelector((state: any) => state.users.error);
  const location = useLocation();

  const createRuleMethods = useForm<any>({
    defaultValues: {},
  });
  const { handleSubmit: handleCreateRuleSubmit, control: createRuleControl } =
    createRuleMethods;

  const [commissionRuleTypes, setCommissionRuleTypes] = useState<
    CommissionRuleType[]
  >([]);
  const [selectedCommissionRuleType, setSelectedCommissionRuleType] = useState<
    CommissionRuleType | undefined
  >();

  const [selectedAgents, setSelectedAgents] = useState<
    { agent: User; value: number }[]
  >([]);
  const [selectedAgentsEmptyError, setSelectedAgentsEmptyError] =
    useState(false);
  const [showError, setShowError] = useState(false);

  const agents = useSelector((state: any) => state.users.users);

  let sumOfValues = () => selectedAgents.reduce((a, b) => a + b.value, 0);
  let currentAgentValue = () => +(createRuleMethods.getValues("value") ?? 0);

  useEffect(() => {
    if (showError) {
      Toast(t(error?.reason ?? "SOMETHING_WENT_WRONG"), t(error?.cause_info));
      setShowError(false);
    }
  }, [showError, error, t]);

  useEffect(() => {
    dispatch(
      fetchCommissionRuleTypes({
        apiPath: "/commission-rule-types",
        includeInactive: false,
      })
    ).then((x) =>
      setCommissionRuleTypes((x.payload as CommissionRuleType[]) ?? [])
    );
  }, [userId]);

  const onCreateRuleSubmit: SubmitHandler<AgentCommissionRule> = async (
    data: AgentCommissionRule
  ) => {
    if (data.carrier_id) {
      data.carrier_id = +data.carrier_id;
    }

    if (data.value) {
      data.value = selectedCommissionRuleType?.is_relative
        ? +data.value / 100
        : +data.value;
    }

    if (data.commission_rule_type_id) {
      data.commission_rule_type_id = +data.commission_rule_type_id;
    }

    if (selectedCommissionRuleType?.id === 4) {
      if (selectedAgents.length === 0) {
        return setSelectedAgentsEmptyError(true);
      }
    }

    let rules: { user_id: number; data: AgentCommissionRule }[] = [];

    if (selectedCommissionRuleType?.id === 4) {
      rules = [
        {
          user_id: userId!,
          data: {
            name: data.name,
            commission_rule_type_id: selectedCommissionRuleType.id,
            value: data.value,
          },
        },
        ...selectedAgents.map((x) => {
          return {
            user_id: x.agent.id!,
            data: {
              commission_rule_type_id: selectedCommissionRuleType?.id,
              value: x.value,
              name: `${data.name} (${x.agent.first_name})`,
            },
          };
        }),
      ];
    } else {
      rules = [
        {
          user_id: userId!,
          data: {
            name: data.name,
            commission_rule_type_id: selectedCommissionRuleType?.id,
            value: data.value,
          },
        },
      ];
    }

    onSaving?.(true);
    dispatch(
      createAgencyUserCommissionRule({
        agencyId: currentUser.agency_id,
        userId: userId!,
        payload: rules,
      })
    ).then((e) => {
        onSaving?.(false);
        if (e.type === "agencies/createAgencyUserCommissionRule/rejected") {
            onSaveCompleted?.(undefined, e.payload as any);
        } else {
            setSelectedAgents([]);
            setSelectedCommissionRuleType(undefined);
            createRuleMethods.reset();
            setSelectedAgentsEmptyError(false);
            onSaveCompleted?.((e.payload as AgentCommissionRule[]).sort((a, b) => a.id! - b.id!), undefined);
        }
    });
  };

  return (
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setSelectedAgents([]);
          setSelectedCommissionRuleType(undefined);
          createRuleMethods.reset();
          setSelectedAgentsEmptyError(false);
          onClose();
        }}
        label={t("NEW_COMMISSION_RULE")}
        onSave={handleCreateRuleSubmit(onCreateRuleSubmit)}
      >
        <div className="flex flex-col space-y-3">
          <div className="w-full flex items-start">
            <div className="md:w-1/2">
              <ReactiveFormInput
                control={createRuleControl}
                label={t("NAME")}
                name="name"
                isRequired
              ></ReactiveFormInput>
            </div>
            <div className="md:w-1/2">
              <ReactiveFormSelect
                control={createRuleControl}
                label={t("COMMISSION_RULE_TYPE")}
                name="comission_rule_type_id"
                options={commissionRuleTypes.map((el) => {
                  return { value: `${el.id}`, label: `${el.name}` };
                })}
                handleOnChange={(e) => {
                  createRuleMethods.setValue("value", "");
                  setSelectedCommissionRuleType(
                    commissionRuleTypes.find((el) => el.id == e.target.value)
                  );
                }}
                isRequired
              ></ReactiveFormSelect>

              <p className="text-slate-500 text-xs px-3 block">
                {`${selectedCommissionRuleType?.description ?? ""}`}
              </p>
            </div>

          </div>

          <div className="w-full flex items-center">
            <ReactiveFormInput
              control={createRuleControl}
              className="md:w-1/4"
              label={t("VALUE")}
              placeholder={
                selectedCommissionRuleType?.is_relative === false ? "$" : "%"
              }
              name="value"
              type="number"
              min={selectedCommissionRuleType?.id !== 3 ? 0 : undefined}
              max={
                selectedCommissionRuleType?.id === 4
                  ? 100 - sumOfValues()
                  : selectedCommissionRuleType?.id === 1
                  ? 100
                  : selectedCommissionRuleType?.id == 3
                  ? -1
                  : undefined
              }
              isRequired
            />
          </div>

          {selectedCommissionRuleType?.id === 4 && (
            <div className="w-full flex flex-col space-y-1">
              <p className="font-medium text-md p-3">{t("OTHER_AGENTS")}</p>
              <div className="w-full divide-y divide-dashed">
                {selectedAgents.map((x) => (
                  <div className="flex p-3">
                    <p className="md:w-1/2">
                      {x.agent.first_name} {x.agent.last_name}
                    </p>
                    <p className="md:w-1/2 font-semibold">{x.value}%</p>
                  </div>
                ))}

                <div className="py-3 w-full flex items-start">
                  <div className="md:w-1/2">
                    <ReactiveFormSelect
                      label={t("AGENT")}
                      name="selectedOtherAgent"
                      control={createRuleControl}
                      options={agents
                        .filter(
                          (x: User) =>
                            x.role_id === RoleEnum.AGENT &&
                            x.id !== userId &&
                            selectedAgents.find((y) => y.agent.id == x.id) ===
                              undefined
                        )
                        .map((x: User) => {
                          return {
                            label: `${x.first_name} ${x.last_name}`,
                            value: `${x.id}`,
                          };
                        })}
                    />
                    {selectedAgentsEmptyError && (
                      <p className="text-red-500 text-xs italic ml-3">
                        {t("NO_OTHER_AGENTS_SELECTED")}
                      </p>
                    )}
                  </div>

                  <ReactiveFormInput
                    control={createRuleControl}
                    className="md:w-1/4"
                    label={t("VALUE")}
                    placeholder={"%"}
                    name="selectedOtherAgentValue"
                    min={1}
                    max={100 - (sumOfValues() + currentAgentValue())}
                    type="number"
                  />

                  <Button
                    classNames="mt-8"
                    onClick={async () => {
                      const result = await createRuleMethods.trigger([
                        "selectedOtherAgent",
                        "selectedOtherAgentValue",
                      ]);

                      if (!result) {
                        return;
                      }

                      let selectedUser = agents.find(
                        (x: User) =>
                          x.id ==
                          createRuleMethods.getValues("selectedOtherAgent")
                      );
                      let value = +createRuleMethods.getValues(
                        "selectedOtherAgentValue"
                      );

                      if (!selectedUser) {
                        createRuleMethods.setError("selectedOtherAgent", {
                          message: t("FIELD_REQUIRED"),
                        });
                        return;
                      }

                      if (isNaN(value)) {
                        createRuleMethods.setError("selectedOtherAgentValue", {
                          message: t("FIELD_REQUIRED"),
                        });
                        return;
                      }

                      createRuleMethods.resetField("selectedOtherAgent");
                      createRuleMethods.resetField("selectedOtherAgentValue", {
                        defaultValue: "",
                      });

                      setSelectedAgentsEmptyError(false);

                      setSelectedAgents((prev) => {
                        let copy = [...prev];
                        copy.push({ agent: selectedUser, value: value });

                        return copy;
                      });
                    }}
                  >
                    <span className="flex items-center font-semibold pr-3">
                      <div className="w-8 p-1 aspect-square mr-2">
                        <PlusIcon />
                      </div>
                      {t("ADD")}
                    </span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
};
